import { theme } from '@Guidelines/Theme/theme';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import MarkingLegend from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentProgress/MarkingLegend';
import FullscreenEnterButton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentProgress/FullscreenEnterButton';
import ZoomControls from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentProgress/ZoomControls';
import VerificationProgressBar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/VerificationProgressBar';
import type VerificationProgressView from '@UI/Store/Documents/Views/VerificationProgressView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';

const cardStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.875rem 1.25rem;
	padding: 1rem 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const summaryStyle = css`
	display: flex;
	flex: 1 1 16rem;
	flex-direction: column;
	gap: 0.5rem;
	min-width: 0;
`;

const summaryTextStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.inkSecondary};
`;

const doneTextStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.states.success.ink};
`;

const legendRowStyle = css`
	flex-basis: 100%;
`;

const countOf = (progress: VerificationProgressView, statusValue: string): number =>
	progress.byStatus.find((entry) => entry.status.value === statusValue)?.count ?? 0;

const pluralize = (count: number, singular: string, plural: string): string =>
	`${count} ${count === 1 ? singular : plural}`;

const formatDesktopSummary = (progress: VerificationProgressView): string => {
	const toVerify = countOf(progress, 'to_verify');
	const verified = countOf(progress, 'verified') + countOf(progress, 'corrected');
	const unverified = countOf(progress, 'unverified');
	const notAddressed = countOf(progress, 'not_addressed');

	return [
		pluralize(toVerify, 'à vérifier', 'à vérifier'),
		pluralize(verified, 'vérifiée', 'vérifiées'),
		unverified > 0 ? pluralize(unverified, 'non vérifiée', 'non vérifiées') : null,
		pluralize(notAddressed, 'non abordé', 'non abordés'),
		progress.invalid > 0 ? pluralize(progress.invalid, 'invalide', 'invalides') : null,
	]
		.filter((part) => part !== null)
		.join(' · ');
};

const formatCompactSummary = (progress: VerificationProgressView): string =>
	progress.remaining === 0 && progress.invalid === 0
		? 'Rien ne reste à vérifier'
		: `${progress.remaining} à vérifier`;

/**
 * The document's advancement: the stacked bar and its counts (pure display, every number read off the
 * server's `progress`), and on desktop the full-screen entry, the zoom and the legend of the viewer's
 * markings. On a phone it shrinks to « {n} à vérifier ».
 */
const DocumentProgress: React.FC = () => {
	const document = useSelector((state) => state.documentsStore.document);
	const isCompact = useIsCompactViewport();

	return (
		<ConditionalLoader data={document}>
			{(loadedDocument) => {
				const { progress } = loadedDocument;
				const isNothingLeft = progress.remaining === 0 && progress.invalid === 0;

				return (
					<div css={cardStyle}>
						<div css={summaryStyle}>
							<VerificationProgressBar byStatus={progress.byStatus} total={progress.total} />
							<span css={summaryTextStyle}>
								{isCompact ? formatCompactSummary(progress) : formatDesktopSummary(progress)}
							</span>
						</div>
						<ConditionalLoader data={!isCompact && isNothingLeft}>
							{() => <span css={doneTextStyle}>Rien ne reste à vérifier.</span>}
						</ConditionalLoader>
						<ConditionalLoader data={!isCompact}>
							{() => (
								<React.Fragment>
									<FullscreenEnterButton />
									<ZoomControls />
									<div css={legendRowStyle}>
										<MarkingLegend />
									</div>
								</React.Fragment>
							)}
						</ConditionalLoader>
					</div>
				);
			}}
		</ConditionalLoader>
	);
};

export default DocumentProgress;
